import { IMapyBoxItem, TActivityType } from "~/interfaces";
import MyMapsDialog, { IMyMapsDialog } from "./mymaps-dialog";
import { _ } from "~/i18n";
import { createMyMapsInput } from "../mymaps/input";
import { createMyMapsNote } from "../mymaps/note";
import { TTripsEmojiMood, createMyMapsTripsEmoji } from "../mymaps/trips-emoji";
import { trackUpdate } from "~/util/providers/mapybox";
import { getMapyboxError, getUserTime, IAnalyticsEdit, mapyboxAnalyticsEdit, mapyboxNotification, updateUserTime } from "~/util/mapybox";
import { MAPYBOX_MAX_DATE, MAPYBOX_MIN_DATE, MAX_TITLE_LEN } from "./const";
import ActivitySelect from "~/ui/activities/select";
import { node } from "~/util/dom";
import DateSelect from "~/ui/dateselect";
import { createMyMapsToggle } from "../mymaps/toggle";
import { openPropositionDialog } from "~/util/premium";

interface IState {
	type: TActivityType;
	usertitle: string;
	description: string;
	mood: TTripsEmojiMood;
	/* eslint-disable camelcase */
	user_time: number;
	/* eslint-enable camelcase */
	visited: boolean;
}

interface IMyMapsEditActivity extends Pick<IMyMapsDialog, "onCancel" | "onClose"> {
	data: IMapyBoxItem;
	isPremiumUser?: boolean;
	onError: () => void;
	onSave: (updData: IMapyBoxItem) => void;
}

/**
 * Aktivita jiz popisek mela.
 */
export default class MyMapsEditActivity {
	_opts: IMyMapsEditActivity;

	_dialog: MyMapsDialog;

	_note: string;

	_state: IState;

	_origState: IState;

	_changed: boolean;

	_focusElement: HTMLTextAreaElement | HTMLInputElement;

	_activitySelect: ActivitySelect;

	constructor(data: IMyMapsEditActivity) {
		this._opts = {
			isPremiumUser: false,
			onSave: () => {},
			onCancel: () => {},
			onClose: () => {},
			onError: () => {},
			...data,
		};
		this._state = {
			type: this._opts.data.data?.info?.trackType || "auto_moto",
			usertitle: this._opts.data.usertitle || this._opts.data.title,
			description: this._opts.data.description || "",
			mood: this._opts.data.mood || "none",
			/* eslint-disable camelcase */
			user_time: getUserTime(this._opts.data),
			/* eslint-enable camelcase */
			visited: this._opts.data.visited || false,
		};
		this._origState = window.structuredClone(this._state);
		this._focusElement = null;
		this._changed = false;

		this._build();
	}

	open() {
		this._dialog?.open();
		this._focusElement?.focus();
	}

	destroy() {
		this._dialog?.destroy();
		this._activitySelect?.destroy();
	}

	_closeDialog(force?: boolean) {
		this._opts.onClose();
		this._dialog.close(force);
	}

	_build() {
		const dialog = new MyMapsDialog({
			titles: {
				main: [_("mymap.contextMenu.editProps")],
			},
			onThrowAwayTest: () => this._checkClose(),
			onThrowAwayConfirm: () => {
				this._closeDialog(true);
			},
			onSave: () => {
				this._onSave();
			},
			onCancel: () => {
				this._opts.onCancel();
				this._closeDialog(true);
			},
		});
		const mainContent = dialog.contents[0];

		mainContent.classList.add("mymaps-dialog__defContent");

		const infoDesc = node("p", { className: "mymaps-dialog__defDesc", textContent: _("mymap.activity.editInfo") });

		mainContent.append(infoDesc);

		this._activitySelect = new ActivitySelect({
			selected: this._state.type,
			onChange: type => {
				// pri editaci by nemelo byt mozne zadat all, ale kdyby se to nahodou stalo, tak to nezaneseme do dat a na server
				if (type === "all") {
					return;
				}

				this._updateState({ type });
			},
		});
		mainContent.append(this._activitySelect.container);

		// titulek
		const titleInput = createMyMapsInput({
			bold: true,
			placeholder: _("mymap.addPopup.name"),
			onChange: usertitle => {
				this._updateState({ usertitle });
			},
			text: this._state.usertitle,
			count: MAX_TITLE_LEN,
		});

		mainContent.append(titleInput.container);
		this._focusElement = titleInput.input;

		// datum
		const dateSelect = new DateSelect({
			date: new Date(this._state.user_time),
			minDate: MAPYBOX_MIN_DATE,
			maxDate: MAPYBOX_MAX_DATE,
		});

		dateSelect.node.addEventListener("datechange", (event: CustomEvent) => {
			// eslint-disable-next-line camelcase
			this._updateState({ user_time: updateUserTime(event.detail.date) });
		});
		mainContent.append(dateSelect.node);

		// poznamka
		if (this._opts.isPremiumUser) {
			const noteData = createMyMapsNote({
				text: this._state.description,
				isPremiumUser: this._opts.isPremiumUser,
				onChange: description => {
					this._updateState({ description });
				},
			});

			mainContent.append(noteData.container);

			if (this._opts.isPremiumUser) {
				this._focusElement = noteData.textarea;
			}
		}

		// nalada
		mainContent.append(createMyMapsTripsEmoji({
			selected: this._state.mood,
			onChange: mood => {
				this._updateState({ mood });
			},
		}));

		mainContent.appendChild(createMyMapsToggle({
			text: !this._opts.isPremiumUser && this._state.visited
				? _("stateSwitch.items2.visit")
				: _("mymap.visited.title"),
			// pro neprihlasene vzdy false!
			checked: this._opts.isPremiumUser
				? this._state.visited || false
				: false,
			isPremiumUser: this._opts.isPremiumUser,
			onChange: visited => {
				this._updateState({ visited });
			},
			onClick: () => {
				if (!this._opts.isPremiumUser) {
					this._closeDialog(true);
					Mapy.getComponent("logger").log("setVisited_premiumPurchase_show");
					openPropositionDialog("setVisited");
				}
			},
		}));

		this._dialog = dialog;
	}

	_updateState(newData: Partial<IState>) {
		Object.assign(this._state, newData);

		this._changed = this._state.type !== this._origState.type
			|| this._state.description !== this._origState.description
			|| this._state.usertitle !== this._origState.usertitle
			/* eslint-disable camelcase */
			|| this._state.user_time !== this._origState.user_time
			/* eslint-enable camelcase */
			|| this._state.mood !== this._origState.mood
			|| this._state.visited !== this._origState.visited;
	}

	async _onSave() {
		// neni zmena, zrusime
		if (!this._changed) {
			this._closeDialog(true);

			return;
		}

		this._dialog.error = "";
		this._dialog.loading = true;

		const analyticsEditData: IAnalyticsEdit = {
			origin: "detail_box_edit",
			type: "activity",
			changed: [],
		};

		try {
			await trackUpdate(this._opts.data.id, {
				"usertitle": this._state.usertitle,
				"description": this._state.description,
				// @ts-ignore
				"data.info.trackType": this._state.type,
				"visited": this._state.visited,
				/* eslint-disable camelcase */
				"user_time": this._state.user_time,
				/* eslint-enable camelcase */
				...this._state.mood === "none"
					? {}
					: {
						mood: this._state.mood,
					},
			});
		} catch (exc) {
			this._dialog.error = getMapyboxError({
				item: this._opts.data,
				operation: "track-update",
			});
			this._dialog.loading = false;
			this._opts.onError();

			return;
		}

		mapyboxNotification({
			item: this._opts.data,
			operation: "track-update",
		});

		// analytika
		const changed = [];

		if (this._state.usertitle !== this._origState.usertitle) {
			changed.push(this._state.usertitle);
			analyticsEditData.changed.push("name");
		}

		if (this._state.description !== this._origState.description) {
			changed.push(this._state.description);
			analyticsEditData.changed.push("note");
			analyticsEditData.note = !!this._state.description;
		}

		if (this._state.mood !== this._origState.mood) {
			changed.push(this._state.mood);
			analyticsEditData.changed.push("mood");
		}

		if (this._state.type !== this._origState.type) {
			changed.push(this._state.type);
			analyticsEditData.changed.push("track_type");
		}

		Mapy.getComponent("logger").log("my_maps_activity_update", { changed });
		mapyboxAnalyticsEdit(analyticsEditData);

		this._closeDialog(true);

		const savedData: IMapyBoxItem = structuredClone(this._opts.data);

		savedData.usertitle = this._state.usertitle;
		savedData.description = this._state.description;
		savedData.data.info.trackType = this._state.type;
		/* eslint-disable camelcase */
		savedData.user_time = this._state.user_time;
		/* eslint-enable camelcase */
		// @ts-ignore
		savedData.mood = this._state.mood;

		this._opts.onSave(savedData);
	}

	_checkClose() {
		return !this._changed;
	}
}
